// Junior leagues: what a minor's registration has to carry.
//
// A league is a junior league because an admin said so on the league form, not
// because of anything derived from the players in it. That keeps the question
// "does this sign-up need a guardian?" answerable from the league alone, before
// anyone has registered.
//
// The rules here are mirrored in functions/index.js (registerForLeague). The
// client copy decides what the form asks for and when the button lights up; the
// server re-validates, because a browser can send whatever it likes.

import { localToday } from './dates';

/** Whether this league collects parent/guardian details at sign-up. */
export const isJuniorLeague = (league) => league?.juniors === true;

/**
 * A birthdate that could belong to a living junior player.
 *
 * Compared as YYYY-MM-DD strings rather than Date objects on purpose: a date of
 * birth is a calendar fact with no time and no zone, and parsing it into a Date
 * is how a birthday drifts a day either side of UTC midnight. String compare on
 * a zero-padded ISO date is the same ordering as date compare, without that.
 */
export function isValidBirthdate(value) {
  const s = String(value || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  // Rejects 31 April and 29 Feb in a common year: a Date built from the parts
  // rolls an impossible day into the next month, so it comes back changed.
  const probe = new Date(y, m - 1, d);
  if (probe.getFullYear() !== y || probe.getMonth() !== m - 1 || probe.getDate() !== d) return false;
  const today = localToday();
  if (s > today) return false;                       // not born yet
  return y >= Number(today.slice(0, 4)) - 120;       // not a typo like 0202
}

/**
 * Age in whole years on a given date (default today, club time).
 * Returns null for anything isValidBirthdate rejects, so callers can render a
 * dash rather than "NaN years old".
 */
export function ageOn(birthdate, onDate = localToday()) {
  if (!isValidBirthdate(birthdate)) return null;
  const [by, bm, bd] = String(birthdate).split('-').map(Number);
  const [oy, om, od] = String(onDate).split('-').map(Number);
  let age = oy - by;
  if (om < bm || (om === bm && od < bd)) age -= 1;   // birthday not reached yet
  return age < 0 ? null : age;
}

/**
 * Age in years to one decimal (13.2), for the profile's "(13.2 years old)".
 * The fraction is how far through the year since the last birthday, and it is
 * cut, not rounded, so nobody reads a year older the week before a birthday.
 * Null where ageOn is null.
 */
export function decimalAgeOn(birthdate, onDate = localToday()) {
  const whole = ageOn(birthdate, onDate);
  if (whole === null) return null;
  const [by, bm, bd] = String(birthdate).split('-').map(Number);
  const [oy, om, od] = String(onDate).split('-').map(Number);
  const on = Date.UTC(oy, om - 1, od);
  const last = Date.UTC(by + whole, bm - 1, bd);
  const next = Date.UTC(by + whole + 1, bm - 1, bd);
  const tenths = Math.floor(((on - last) / (next - last)) * 10);
  return Math.min(whole + tenths / 10, whole + 0.9);
}

/**
 * What is still missing before a junior registration can be submitted.
 * Empty array means good to go — and for a league that isn't a junior league,
 * it is always empty, so callers can apply it unconditionally.
 */
export function missingJuniorFields(league, { guardianName, birthdate }) {
  if (!isJuniorLeague(league)) return [];
  const missing = [];
  if (!String(guardianName || '').trim()) missing.push('parent or guardian name');
  if (!isValidBirthdate(birthdate)) missing.push('date of birth');
  return missing;
}
